import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSchemeExits,
  getExitRequest,
  requestExit,
  computeSettlement,
  rejectExit,
  downloadNoc,
} from './network';

export const foreclosureKeys = {
  all: ['foreclosure'] as const,
  lists: () => [...foreclosureKeys.all, 'list'] as const,
  list: (schemeId: string) => [...foreclosureKeys.lists(), schemeId] as const,
  details: () => [...foreclosureKeys.all, 'detail'] as const,
  detail: (id: string) => [...foreclosureKeys.details(), id] as const,
  settlements: () => [...foreclosureKeys.all, 'settlement'] as const,
  settlement: (id: string) => [...foreclosureKeys.settlements(), id] as const,
};

export function useExitRequests(schemeId: string) {
  return useQuery({
    queryKey: foreclosureKeys.list(schemeId),
    queryFn: () => listSchemeExits(schemeId),
    enabled: !!schemeId,
  });
}

export function useExitRequest(id: string) {
  return useQuery({
    queryKey: foreclosureKeys.detail(id),
    queryFn: () => getExitRequest(id),
    enabled: !!id,
  });
}

// Satisfying spec 5.1: useSettlementQuote(id, enabled) using the detail endpoint where breakdown is returned
export function useSettlementQuote(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: foreclosureKeys.settlement(id),
    queryFn: () => getExitRequest(id),
    enabled: enabled && !!id,
  });
}

export function useRequestExit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, subscriberId, schemeId }: { subscriptionId?: string; subscriberId?: string; schemeId: string }) =>
      requestExit((subscriptionId || subscriberId)!, { schemeId }),
    onSuccess: (_, { schemeId }) => {
      queryClient.invalidateQueries({ queryKey: foreclosureKeys.lists() });
    },
  });
}

export function useComputeSettlement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => computeSettlement(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: foreclosureKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: foreclosureKeys.settlement(id) });
      queryClient.invalidateQueries({ queryKey: foreclosureKeys.lists() });
    },
  });
}

export function useRejectExit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectExit(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: foreclosureKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: foreclosureKeys.lists() });
    },
  });
}

export function useDownloadNoc() {
  return useMutation({
    mutationFn: (id: string) => downloadNoc(id),
  });
}
